import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { PAYMENT_SINGLE_MONTH_QUERY_KEY } from 'src/contexts/MarketplacePaymentDataContext/MarketplacePaymentDataContext.constants';
import { useConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog/useConfirmDialog';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MarketplacePaymentClient,
  MarketplaceSalePayment,
} from 'src/WebApiController';

export const useLockUnlockPaymentAction = (
  isLoading: boolean,
  setIsLoading: (v: boolean) => void,
  marketplacePayment: MarketplaceSalePayment
) => {
  const queryClient = useQueryClient();
  const { activeAccountWebClientConfig } = useAppContext();
  const { openConfirmDialog, closeConfirmDialog } = useConfirmDialog();
  const { showErrorDialog } = useClientLogContext();

  const onSave = useCallback(async () => {
    setIsLoading(true);
    try {
      await new MarketplacePaymentClient(
        activeAccountWebClientConfig
      ).updateMarketplacePaymentIsLocked(
        marketplacePayment.id,
        !marketplacePayment.isLocked
      );

      const isExpanded = true;
      queryClient.invalidateQueries({
        queryKey: [PAYMENT_SINGLE_MONTH_QUERY_KEY, isExpanded],
        refetchType: 'active',
      });

      closeConfirmDialog();
    } catch (e) {
      showErrorDialog(
        'MarketplacePaymentClient.updateMarketplacePaymentIsLocked',
        e as Error
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    activeAccountWebClientConfig,
    closeConfirmDialog,
    marketplacePayment.id,
    marketplacePayment.isLocked,
    queryClient,
    setIsLoading,
    showErrorDialog,
  ]);

  const onLockUnlockClicked = useCallback(() => {
    openConfirmDialog({
      size: 'm',
      headerText: (
        <Content
          id={
            marketplacePayment.isLocked
              ? ContentId.UnlockPayment
              : ContentId.LockPayment
          }
        />
      ),
      bodyText: (
        <Content
          id={
            marketplacePayment.isLocked
              ? ContentId.UnlockPaymentConfirmation
              : ContentId.LockPaymentConfirmation
          }
        />
      ),
      onOkay: () => onSave(),
      onCancel: closeConfirmDialog,
      okText: ContentId.Yes,
      cancelText: ContentId.No,
    });
  }, [
    closeConfirmDialog,
    marketplacePayment.isLocked,
    onSave,
    openConfirmDialog,
  ]);

  return {
    dropdown: (
      <PosDropdownItem
        key="lock-unlock-marketplace-payment"
        onClick={onLockUnlockClicked}
        disabled={isLoading}
      >
        <Content
          id={marketplacePayment.isLocked ? ContentId.Unlock : ContentId.Lock}
        />
      </PosDropdownItem>
    ),
  };
};
